import type { RefObject } from 'react';

import { useFilterPanelContext } from './FilterPanelContext';
import {
  type ActiveFilterEntry,
  type FilterDefinition,
  useFilters,
} from './provider';
import { serializeFilterValue } from './serializeFilterValue';

type FilterPanelState = {
  filters: FilterDefinition[];
  activeFilters: ActiveFilterEntry[];
  filterIds: Set<string>;
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  panelId: string;
  triggerRef: RefObject<HTMLButtonElement>;
};

export const useFilterPanel = ({
  filters: filtersOverride,
}: { filters?: FilterDefinition[] } = {}): FilterPanelState => {
  const { filters: contextFilters, activeFilters } = useFilters();
  const filters = filtersOverride ?? contextFilters;
  const filterIds = new Set(filters.map(filter => filter.id));
  const activePanelFilters = activeFilters.filter(filter =>
    filterIds.has(filter.id),
  );
  const context = useFilterPanelContext();
  const hasNonDefaultFilters = activePanelFilters.some(
    filter =>
      !context.defaultFilters.some(defaultFilter => {
        if (defaultFilter.id !== filter.id) return false;
        const query = filter.buildQuery();
        const defaultQuery = filter.activate(defaultFilter.value).buildQuery();
        return query || defaultQuery
          ? query === defaultQuery
          : serializeFilterValue(filter.value) ===
              serializeFilterValue(defaultFilter.value);
      }),
  );
  const isOpen =
    context.openPreference ??
    (context.defaultOpen || context.pinnedOpen || hasNonDefaultFilters);
  return {
    filters,
    activeFilters: activePanelFilters,
    filterIds,
    isOpen,
    toggle: () => {
      context.onToggle();
      context.clearPin();
      context.setOpenPreference(!isOpen);
    },
    open: context.pinOpen,
    panelId: context.panelId,
    triggerRef: context.triggerRef,
  };
};
